import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

type SetSearchParamsFunction = (
    nextInit: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    navigateOptions?: { replace?: boolean; state?: any }
) => void;

const updateQueue: ((prev: URLSearchParams) => URLSearchParams)[] = [];
let flushTimeout: NodeJS.Timeout | null = null;
let originalSetSearchParams: SetSearchParamsFunction | null = null;

const flushUpdates = () => {
    if (originalSetSearchParams && updateQueue.length > 0) {
        originalSetSearchParams((currentParams) => {
            return updateQueue.reduce((params, updateFn) => updateFn(params), new URLSearchParams(currentParams));
        });

        updateQueue.length = 0;
    }
    flushTimeout = null;
};

export const usePatchedSearchParams = () => {
    /*
    This hook patches the useSearchParams hook to allow for batched updates.
    E.g. multiple calls to setSearchParams in a single render
    */
    const [searchParams, setSearchParams] = useSearchParams();

    originalSetSearchParams = setSearchParams;

    const batchedSetSearchParams: SetSearchParamsFunction = useCallback((nextInit, navigateOptions) => {
        if (typeof nextInit === 'function') {
            updateQueue.push(nextInit);
        } else {
            updateQueue.push(() => nextInit);
        }

        if (flushTimeout) {
            clearTimeout(flushTimeout);
        }

        flushTimeout = setTimeout(flushUpdates, 0);
    }, []);

    return [searchParams, batchedSetSearchParams] as const;
};

const useSearchParamState = <T,>(key, fallbackValue = undefined): [string, (newValue: string) => void] => {
    const [searchParams, setSearchParams] = usePatchedSearchParams();
    const value = searchParams.get(key) || fallbackValue;

    const setState = (newValue) => {
        setSearchParams((params) => {
            if (newValue) {
                params.set(key, newValue);
            } else {
                params.delete(key);
            }
            return params;
        });
    };

    return [value, setState];
};

export const useBooleanSearchParamState = (key, fallbackValue = false): [boolean, (newValue: boolean) => void] => {
    const [searchParams, setSearchParams] = usePatchedSearchParams();
    const value = searchParams.get(key) ? searchParams.get(key) === 'true' : fallbackValue;

    const setState = (newValue) => {
        setSearchParams((params) => {
            if (newValue) {
                params.set(key, 'true');
            } else {
                params.set(key, 'false');
            }
            return params;
        });
    };

    return [value, setState];
};

export const useArraySearchParamState = (
    key: string,
    fallbackValue: string[] = [],
    emptyValue: string = ''
): [string[], (newValue: string[]) => void] => {
    const [searchParams, setSearchParams] = usePatchedSearchParams();

    const currentValue = searchParams.get(key);
    const value = currentValue ? currentValue.split(',').filter((value) => value !== emptyValue) : fallbackValue;

    const setState = useCallback(
        (newValue: string[]) => {
            setSearchParams((params) => {
                if (newValue && newValue.length > 0) {
                    params.set(key, newValue.join(','));
                } else {
                    params.set(key, emptyValue);
                }
                return params;
            });
        },
        [key, emptyValue, setSearchParams]
    );

    return [value, setState];
};

export default useSearchParamState;
